import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, TextField, IconButton, Select, MenuItem, Tooltip, Popper, Paper, Typography, Chip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import DescriptionIcon from '@mui/icons-material/Description';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';

export default function ChatInput({
  onSend,
  isStreaming,
  onStop,
  model,
  onModelChange,
  models,
  onOpenPrompts,
  onOpenAgents,
  agents = [],
  clients = [],
  selectedClientId,
  onClientChange,
  agentToInsert,
  onAgentInserted,
  compact = false,
}) {
  const [message, setMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Insert agent mention when agentToInsert changes
  useEffect(() => {
    if (agentToInsert) {
      setMessage((prev) => {
        const prefix = prev && !prev.endsWith(' ') ? prev + ' ' : prev;
        return `${prefix}@${agentToInsert.name} `;
      });
      if (onAgentInserted) onAgentInserted();
      setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
    }
  }, [agentToInsert, onAgentInserted]);

  // @-mention autocomplete state
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1); // cursor position of the '@'

  const filteredAgents = agents.filter((a) =>
    a.name.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const closeMention = useCallback(() => {
    setMentionAnchor(null);
    setMentionQuery('');
    setMentionIndex(0);
    setMentionStart(-1);
  }, []);

  // Detect @ while typing
  const handleChange = (e) => {
    const val = e.target.value;
    setMessage(val);

    const el = e.target;
    const cursor = el.selectionStart;

    // Look backwards from cursor for an unmatched '@'
    const textBefore = val.slice(0, cursor);
    const atIdx = textBefore.lastIndexOf('@');

    if (atIdx !== -1) {
      // Only trigger if @ is at start or preceded by whitespace
      const charBefore = atIdx > 0 ? val[atIdx - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || atIdx === 0) {
        const query = textBefore.slice(atIdx + 1);
        // No spaces in agent names for matching purposes; close if user typed a space after query
        if (!query.includes(' ')) {
          setMentionQuery(query);
          setMentionStart(atIdx);
          setMentionIndex(0);
          setMentionAnchor(el);
          return;
        }
      }
    }
    closeMention();
  };

  // Insert selected agent into text
  const insertMention = useCallback((agent) => {
    const before = message.slice(0, mentionStart);
    const after = message.slice(mentionStart + 1 + mentionQuery.length);
    const newMessage = `${before}@${agent.name} ${after}`;
    setMessage(newMessage);
    closeMention();
    // Refocus
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const pos = mentionStart + agent.name.length + 2; // +2 for @ and space
        inputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  }, [message, mentionStart, mentionQuery, closeMention]);

  const handleSend = () => {
    const trimmed = message.trim();
    if ((!trimmed && attachedFiles.length === 0) || isStreaming) return;

    // Parse @AgentName from message to find mentioned agent
    let mentionedAgentId = null;
    const mentionRegex = /@(\S+)/;
    const match = trimmed.match(mentionRegex);
    if (match) {
      const mentionName = match[1];
      const agent = agents.find((a) => a.name === mentionName);
      if (agent) mentionedAgentId = agent.id;
    }

    onSend(trimmed || '(bestanden bijgevoegd)', mentionedAgentId, attachedFiles);
    setMessage('');
    setAttachedFiles([]);
    closeMention();
  };

  const handleKeyDown = (e) => {
    // Handle autocomplete keyboard navigation
    if (mentionAnchor && filteredAgents.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % filteredAgents.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + filteredAgents.length) % filteredAgents.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredAgents[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMention();
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;
    setAttachedFiles((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  // Close autocomplete when clicking outside
  useEffect(() => {
    if (!mentionAnchor) return;
    const handleClick = (e) => {
      if (!e.target.closest('.mention-popup')) closeMention();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mentionAnchor, closeMention]);

  const mentionOpen = Boolean(mentionAnchor) && filteredAgents.length > 0;

  return (
    <Box sx={{ borderTop: '1px solid rgba(45,51,53,0.08)', bgcolor: '#fff', p: 2 }}>
      {/* Toolbar row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Tooltip title="Agents">
          <IconButton size="small" onClick={onOpenAgents} sx={{ color: '#767c7e' }}>
            <SmartToyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Prompt templates">
          <IconButton size="small" onClick={onOpenPrompts} sx={{ color: '#767c7e' }}>
            <DescriptionIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!compact && clients.length > 0 && onClientChange && (
          <Select
            value={selectedClientId || ''}
            onChange={(e) => onClientChange(e.target.value || null)}
            size="small"
            displayEmpty
            sx={{
              fontSize: 13, height: 32, minWidth: 140,
              '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 },
            }}
            renderValue={(val) => {
              if (!val) return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#9ca3af' }}><BusinessIcon sx={{ fontSize: 14 }} /> Klant</Box>;
              const cl = clients.find((c) => c.id === val);
              return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {cl?.logo ? <Box component="img" src={cl.logo} alt="" sx={{ width: 14, height: 14, borderRadius: 0.25, objectFit: 'contain' }} /> : <BusinessIcon sx={{ fontSize: 14 }} />}
                {cl?.name || 'Klant'}
              </Box>;
            }}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>Geen klant</MenuItem>
            {clients.map((c) => (
              <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {c.logo ? <Box component="img" src={c.logo} alt="" sx={{ width: 16, height: 16, borderRadius: 0.25, objectFit: 'contain' }} /> : <BusinessIcon sx={{ fontSize: 14, color: '#9ca3af' }} />}
                {c.name}
              </MenuItem>
            ))}
          </Select>
        )}
        {!compact && (
          <Select
            value={model}
            onChange={(e) => onModelChange(e.target.value)}
            size="small"
            sx={{
              fontSize: 13,
              height: 32,
              minWidth: 140,
              '& .MuiSelect-select': { py: 0.5 },
            }}
          >
            {models.map((m) => (
              <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>

      {/* Attached files chips */}
      {attachedFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {attachedFiles.map((file, idx) => (
            <Chip
              key={`${file.name}-${idx}`}
              icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
              label={`${file.name} (${formatFileSize(file.size)})`}
              size="small"
              onDelete={() => removeFile(idx)}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                fontSize: 12, height: 26,
                bgcolor: '#d5e3fd', color: '#465469',
                '& .MuiChip-deleteIcon': { color: '#526075', '&:hover': { color: '#465469' } },
              }}
            />
          ))}
        </Box>
      )}

      {/* Input row */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', position: 'relative' }}>
        {/* File upload button */}
        <Tooltip title="Bestand toevoegen">
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming}
            sx={{
              color: '#adb3b5',
              border: '1px solid rgba(45,51,53,0.12)',
              borderRadius: '50%',
              '&:hover': { color: '#526075', bgcolor: '#f1f4f5' },
              width: 36, height: 36, mb: 0.25,
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept=".txt,.md,.pdf,.csv,.json,.xml,.yaml,.yml,.log,.py,.js,.jsx,.ts,.tsx,.html,.css,.doc,.docx"
          onChange={handleFileSelect}
        />

        <TextField
          inputRef={inputRef}
          multiline
          maxRows={6}
          fullWidth
          placeholder="Typ een bericht... (@agent om een agent aan te spreken)"
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '16px',
              fontSize: 14,
              bgcolor: '#f8f9fa',
              '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
              '&:hover fieldset': { borderColor: '#adb3b5' },
              '&.Mui-focused': { bgcolor: '#fff' },
              '&.Mui-focused fieldset': { borderColor: '#526075', borderWidth: '1.5px' },
            },
          }}
        />

        {/* @-mention autocomplete popup */}
        <Popper
          open={mentionOpen}
          anchorEl={mentionAnchor}
          placement="top-start"
          style={{ zIndex: 1300 }}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <Paper
            className="mention-popup"
            elevation={8}
            sx={{
              maxHeight: 240,
              overflowY: 'auto',
              minWidth: 220,
              maxWidth: 320,
              border: '1px solid rgba(45,51,53,0.08)',
              borderRadius: 3,
            }}
          >
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(45,51,53,0.06)' }}>
              <Typography variant="caption" sx={{ color: '#adb3b5', fontWeight: 600 }}>
                Agents
              </Typography>
            </Box>
            {filteredAgents.map((agent, idx) => (
              <Box
                key={agent.id}
                onClick={() => insertMention(agent)}
                sx={{
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: idx === mentionIndex ? '#d5e3fd' : 'transparent',
                  '&:hover': { bgcolor: '#f1f4f5' },
                }}
              >
                <SmartToyIcon sx={{ fontSize: 18, color: agent.color || '#526075' }} />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#2d3335' }}>
                    {agent.name}
                  </Typography>
                  {agent.description && (
                    <Typography sx={{ fontSize: 11, color: '#adb3b5' }}>
                      {agent.description.length > 50 ? agent.description.slice(0, 50) + '...' : agent.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Paper>
        </Popper>

        {isStreaming ? (
          <IconButton
            onClick={onStop}
            sx={{
              bgcolor: '#f97316',
              color: '#fff',
              borderRadius: '50%',
              '&:hover': { bgcolor: '#ea580c' },
              width: 40,
              height: 40,
            }}
          >
            <StopIcon />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleSend}
            disabled={!message.trim() && attachedFiles.length === 0}
            sx={{
              bgcolor: '#526075',
              color: '#fff',
              borderRadius: '50%',
              '&:hover': { bgcolor: '#465469' },
              '&.Mui-disabled': { bgcolor: '#f1f4f5', color: '#adb3b5' },
              width: 40,
              height: 40,
            }}
          >
            <SendIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
